import React, { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { FiEdit2 } from "react-icons/fi";
import { MdDeleteForever } from "react-icons/md";
import { AiOutlineEye } from "react-icons/ai";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import { getClientsApi, deleteClientApi } from "../../../../Api/ClientWork/ClientWork";
import ClientDetailModal from "./ClientDetailModal";

/**
 * Clients Table Screen (Client Work Module)
 * Matching project standard theme, ag-grid layout, and ActionButton
 */
const ClientsTable = () => {
  const history = useHistory();
  const gridRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [columnVisibility, setColumnVisibility] = useState({});
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const loadData = useCallback(async () => {
    const result = await getClientsApi();
    if (result?.data?.IsSuccess === true) {
      setClients(result.data.Data || []);
    } else {
      toast.error(result?.data?.Message || "Could not load clients");
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = useCallback(async (id, name) => {
    if (window.confirm(`Are you sure you want to delete client "${name || "this client"}"?`)) {
      const result = await deleteClientApi(id);
      if (result?.data?.IsSuccess === true) {
        setClients((prev) => prev.filter((c) => c.Id !== id));
        toast.success("Client deleted successfully");
      } else {
        toast.error(result?.data?.Message || "Could not delete client");
      }
    }
  }, []);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        filter: false,
        sortable: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Owner / Company Name",
        field: "OwnerName",
        flex: 2,
        minWidth: 220,
        cellRenderer: (params) => (
          <span
            style={{
              fontWeight: 600,
              color: "var(--color-primary, #0b6462)",
              cursor: "pointer",
            }}
            title="Click to view client details"
            onClick={() => {
              setSelectedClient(params.data);
              setIsDetailModalOpen(true);
            }}
          >
            {params.value || "—"}
          </span>
        ),
      },
      {
        headerName: "NTN",
        field: "NTN",
        flex: 1,
        minWidth: 120,
      },
      {
        headerName: "Contact Person",
        field: "ContactPerson",
        flex: 1.2,
        minWidth: 150,
      },
      {
        headerName: "Mobile / WhatsApp",
        field: "Mobile",
        flex: 1.1,
        minWidth: 140,
      },
      {
        headerName: "Business Type",
        field: "BusinessType",
        flex: 1.2,
        minWidth: 160,
      },
      {
        headerName: "Tax Status",
        field: "TaxStatus",
        flex: 0.9,
        minWidth: 110,
        cellRenderer: (params) => {
          const isFiler = params.value === "Filer";
          return (
            <span
              style={{
                backgroundColor: isFiler ? "#e6f4ea" : "#fce8e6",
                color: isFiler ? "#137333" : "#c5221f",
                padding: "2px 8px",
                borderRadius: "10px",
                fontSize: "11px",
                fontWeight: 600,
              }}
            >
              {params.value || "N/A"}
            </span>
          );
        },
      },
      {
        headerName: "City",
        field: "City",
        flex: 0.9,
        minWidth: 110,
      },
      {
        headerName: "Status",
        field: "Status",
        flex: 0.9,
        minWidth: 100,
        cellRenderer: (params) => {
          const isActive = params.value === "Active";
          return (
            <span
              style={{
                backgroundColor: isActive ? "#d4edda" : "#fff3cd",
                color: isActive ? "#155724" : "#856404",
                padding: "2px 10px",
                borderRadius: "12px",
                fontSize: "11px",
                fontWeight: 600,
              }}
            >
              {params.value || "Active"}
            </span>
          );
        },
      },
      {
        headerName: "Actions",
        colId: "actions",
        width: 140,
        filter: false,
        sortable: false,
        suppressMenu: true,
        headerComponent: () => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <ActionButton
              text="New+"
              newBtn="true"
              onClick={() => history.push("/client-work/clients/add")}
            />
          </div>
        ),
        cellRenderer: (params) => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "12px",
              height: "100%",
            }}
          >
            <AiOutlineEye
              title="View Client Details"
              size="1.25em"
              style={{ cursor: "pointer", color: "#075e5c" }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedClient(params.data);
                setIsDetailModalOpen(true);
              }}
            />
            <FiEdit2
              title="Edit Client"
              size="1.1em"
              style={{ cursor: "pointer", color: "#4ac8d1" }}
              onClick={(e) => {
                e.stopPropagation();
                history.push(`/client-work/clients/edit/${params.data?.Id}`);
              }}
            />
            <MdDeleteForever
              title="Delete Client"
              size="1.3em"
              style={{ cursor: "pointer", color: "#dc3545" }}
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(params.data?.Id, params.data?.OwnerName);
              }}
            />
          </div>
        ),
      },
    ],
    [history, handleDelete],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      tooltipValueGetter: (p) => p.value,
      valueFormatter: (p) =>
        p.value === null || p.value === undefined || p.value === "" ? "—" : p.value,
      minWidth: 100,
    }),
    [],
  );

  const onGridReady = useCallback((params) => {
    const map = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        map[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(map);
  }, []);

  const toggleColumn = useCallback(
    (colId) => {
      if (!gridRef.current?.api) {
        return;
      }
      const isCurrentlyVisible = columnVisibility[colId];
      gridRef.current.api.setColumnsVisible([colId], !isCurrentlyVisible);
      setColumnVisibility((prev) => ({ ...prev, [colId]: !isCurrentlyVisible }));
    },
    [columnVisibility],
  );

  return (
    <div className="pt-2">
      {/* ── Filter Toolbar ── */}
      <div
        className="d-flex flex-wrap align-items-center justify-content-end pt-3 app_font w-100"
        style={{ gap: "12px", width: "100%" }}
      >
        <div
          className="d-flex align-items-center"
          style={{
            minHeight: "30px",
            maxWidth: "100%",
            marginRight: "auto",
            padding: "4px 12px",
            color: "var(--btn-comp-text, #0b6462)",
            fontSize: "var(--font-md)",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
          title="Clients"
        >
          Clients
        </div>
        <SearchFilter
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search Clients by Name, NTN, Mobile..."
        />
        <ColumnVisibilityControl
          gridApi={gridRef.current?.api}
          columnVisibility={columnVisibility}
          toggleColumn={toggleColumn}
        />
      </div>

      {/* ── Table Grid ── */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div className="ag-theme-alpine vouchers_table_height2 mt-3" style={{ width: "100%" }}>
            <AgGridReact
              ref={gridRef}
              rowData={clients}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              quickFilterText={searchTerm}
              pagination={true}
              paginationPageSize={15}
              tooltipShowDelay={0}
              onGridReady={onGridReady}
              onRowDoubleClicked={(e) => {
                setSelectedClient(e.data);
                setIsDetailModalOpen(true);
              }}
              rowStyle={{ cursor: "pointer" }}
            />
          </div>
        </div>
      </div>

      {/* ── Client Detail Modal ── */}
      {isDetailModalOpen && (
        <ClientDetailModal
          isOpen={isDetailModalOpen}
          onClose={() => {
            setIsDetailModalOpen(false);
            setSelectedClient(null);
          }}
          clientData={selectedClient}
        />
      )}
    </div>
  );
};

export default ClientsTable;
